export interface ParsedUrl {
  bucketName: string;
  bucketId: string;
  filePath: string;
  filename: string;
  fullFilePath: string;
  contractId: string;
  email: string;
  uuid: string;
}

/**
 * Parses an S3 URL to extract bucket name, bucket ID, file path, filename,
 * full file path, contract ID, email, and UUID from query parameters
 */
export const parseS3Url = (url: string): ParsedUrl | null => {
  try {
    // Split URL into base and query parameters
    const [urlWithoutQuery, queryString] = url.split('?');

    // Extract protocol and domain
    const urlObj = new URL(urlWithoutQuery);
    const hostname = urlObj.hostname;

    // Extract bucket name (from https:// to .com)
    const bucketName = hostname.split('.')[0];

    // Extract path after domain
    const pathParts = urlObj.pathname
      .split('/')
      .filter((part) => part.length > 0);

    // First part is bucket ID
    const bucketId = pathParts[0];

    // Rest is file path
    const filePathParts = pathParts.slice(1);
    const filePath = filePathParts.join('/');

    // Filename is the last part before the extension
    const filename = filePathParts[filePathParts.length - 1];

    // Full file path from bucket name to just before filename
    const fullFilePath = `${bucketName}.s3.amazonaws.com/${bucketId}/${filePathParts.slice(0, -1).join('/')}`;

    // Parse query parameters
    const queryParams = new URLSearchParams(queryString);
    const contractId = queryParams.get('contractId') || '';
    const email = queryParams.get('email') || '';
    const uuid = queryParams.get('uuid') || '';

    return {
      bucketName,
      bucketId,
      filePath,
      filename,
      fullFilePath,
      contractId,
      email,
      uuid,
    };
  } catch (error) {
    console.error('Error parsing S3 URL:', error);
    return null;
  }
};

/**
 * Extracts the pathUrl parameter from the current window URL
 */
export const extractPathUrlFromWindow = (): string | null => {
  const fullUrl = window.location.href;
  const pathUrlParam = fullUrl.split('pathUrl=')[1];

  if (pathUrlParam) {
    // Decode the URL parameter if it's encoded
    return pathUrlParam;
  }

  return null;
};

/**
 * Cleans the pathUrl by removing everything from contractId onwards
 * Returns only the base S3 URL without query parameters
 */
export const cleanPathUrl = (pathUrl: string): string => {
  // Remove everything from 'contractId' onwards
  const contractIdIndex = pathUrl.indexOf('contractId=');
  if (contractIdIndex !== -1) {
    return pathUrl.substring(0, contractIdIndex - 1); // -1 to remove the '?' before contractId
  }
  return pathUrl;
};

/**
 * Creates a download link for a PDF blob
 */
export const downloadPdfBlob = (
  pdfBytes: Uint8Array,
  filename: string = 'signed-contract.pdf'
): void => {
  const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

/**
 * Validates if a parsed URL contains all required fields
 */
export const validateParsedUrl = (parsedUrl: ParsedUrl | null): boolean => {
  if (!parsedUrl) return false;

  return !!(
    parsedUrl.bucketName &&
    parsedUrl.bucketId &&
    parsedUrl.filePath &&
    parsedUrl.filename &&
    parsedUrl.contractId
  );
};

/**
 * Logs parsed URL components for debugging
 */
export const logParsedUrlComponents = (parsedUrl: ParsedUrl): void => {
  // Debug logging removed
};
